import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiBaseUrl, apiRequest, authDisabled, msalInstance } from '../auth/authConfig';
import type {
  CreatedPoster,
  CreatePosterFromTemplateRequest,
  CreatePosterRequest,
  Match,
  MatchRequest,
  Poster,
  PosterTemplateDefinition,
  ProviderInfo,
  Tournament,
  TournamentRequest,
} from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly validation?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function accessToken(): Promise<string | null> {
  if (authDisabled || apiRequest.scopes.length === 0) {
    return null;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (error) {
    // Silent renewal runs in a hidden iframe, and that iframe fails for reasons the user cannot
    // act on: consent lapsed (InteractionRequiredAuthError), or the iframe never came back at all
    // (BrowserAuthError - monitor_window_timeout, usually a browser blocking third-party cookies).
    // Both are recoverable by asking interactively, so neither should reach the user as an error.
    if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
      const result = await msalInstance.acquireTokenPopup(apiRequest);
      return result.accessToken;
    }
    throw error;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();
  const headers = new Headers(init.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    throw toApiError(payload, response.status);
  }

  return payload as T;
}

function toApiError(payload: unknown, status: number): ApiError {
  const problem = payload as { title?: string; detail?: string; errors?: Record<string, string[]> } | undefined;
  const validation = problem?.errors;
  const firstValidationMessage = validation ? Object.values(validation).flat()[0] : undefined;

  return new ApiError(
    firstValidationMessage ?? problem?.detail ?? problem?.title ?? `Request failed with status ${status}`,
    status,
    validation,
  );
}

export const api = {
  providers: () => request<ProviderInfo[]>('/api/providers'),

  posterTemplates: () => request<PosterTemplateDefinition[]>('/api/poster-templates'),

  createPosterFromTemplate: (body: CreatePosterFromTemplateRequest) =>
    request<CreatedPoster>('/api/posters', { method: 'POST', body: JSON.stringify(body) }),

  listTournaments: (season?: string) =>
    request<Tournament[]>(`/api/tournaments${season ? `?season=${encodeURIComponent(season)}` : ''}`),

  getTournament: (id: string) => request<Tournament>(`/api/tournaments/${id}`),

  createTournament: (body: TournamentRequest) =>
    request<Tournament>('/api/tournaments', { method: 'POST', body: JSON.stringify(body) }),

  updateTournament: (id: string, body: TournamentRequest) =>
    request<Tournament>(`/api/tournaments/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteTournament: (id: string) => request<void>(`/api/tournaments/${id}`, { method: 'DELETE' }),

  uploadBackground: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Tournament>(`/api/tournaments/${id}/background`, { method: 'POST', body: form });
  },

  listMatches: (tournamentId: string) => request<Match[]>(`/api/tournaments/${tournamentId}/matches`),

  createMatch: (tournamentId: string, body: MatchRequest) =>
    request<Match>(`/api/tournaments/${tournamentId}/matches`, { method: 'POST', body: JSON.stringify(body) }),

  updateMatch: (tournamentId: string, matchId: string, body: MatchRequest) =>
    request<Match>(`/api/tournaments/${tournamentId}/matches/${matchId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteMatch: (tournamentId: string, matchId: string) =>
    request<void>(`/api/tournaments/${tournamentId}/matches/${matchId}`, { method: 'DELETE' }),

  listPosters: (tournamentId: string) => request<Poster[]>(`/api/tournaments/${tournamentId}/posters`),

  createPoster: (tournamentId: string, body: CreatePosterRequest) =>
    request<Poster>(`/api/tournaments/${tournamentId}/posters`, { method: 'POST', body: JSON.stringify(body) }),

  selectPosterVersion: (tournamentId: string, posterId: string, versionId: string) =>
    request<Poster>(
      `/api/tournaments/${tournamentId}/posters/${posterId}/versions/${versionId}/select`,
      { method: 'POST' },
    ),

  regeneratePoster: (tournamentId: string, posterId: string) =>
    request<Poster>(`/api/tournaments/${tournamentId}/posters/${posterId}/regenerate`, { method: 'POST' }),

  deletePoster: (tournamentId: string, posterId: string) =>
    request<void>(`/api/tournaments/${tournamentId}/posters/${posterId}`, { method: 'DELETE' }),
};
